import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import styled from 'styled-components';

import type { EvaluationResult } from 'types/model/fraction';
import { addTestingIdentifier } from 'utils/testing';

import Fraction from '../models/Fraction';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const Buttons = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const Detail = styled.small`
  white-space: nowrap;
`;

export interface SubdivisionEvaluationProps {
  fraction: Fraction;
  disabled?: boolean;
}

// PLA-860 v2 (V2, V11): resultado simple de una subdivision. Solo hace falta marcar las
// rechazadas: las que queden sin resultado se aprueban al confirmar las fracciones.
const SubdivisionEvaluation = ({ fraction, disabled }: SubdivisionEvaluationProps) => {
  const [errors, setErrors] = useState<string[]>([]);

  const handleClick = async (result: EvaluationResult) => {
    if (fraction.evaluationResult === result && !fraction.evaluationImplicit) {
      return;
    }

    setErrors(await fraction.evaluate(result));
  };

  const buttonClasses = (result: EvaluationResult) =>
    classNames('button', 'button--small', {
      'button--green': result === 'approved' && fraction.evaluationResult === result,
      'button--red': result === 'rejected' && fraction.evaluationResult === result,
      'button--ghost': fraction.evaluationResult !== result,
    });

  const detail = fraction.evaluationImplicit
    ? I18n.translate('fractions.subdivision.implicit_approval')
    : fraction.evaluatedByName &&
      I18n.translate('fractions.subdivision.evaluated_by', {
        user: fraction.evaluatedByName,
        date: fraction.evaluatedAt,
      });

  return (
    <Wrapper>
      <Buttons>
        <button
          type='button'
          className={buttonClasses('approved')}
          disabled={disabled || fraction.requestPending}
          onClick={() => handleClick('approved')}
          {...addTestingIdentifier('subdivision_approve')}
        >
          {I18n.translate('fractions.subdivision.approved')}
        </button>
        <button
          type='button'
          className={buttonClasses('rejected')}
          disabled={disabled || fraction.requestPending}
          onClick={() => handleClick('rejected')}
          {...addTestingIdentifier('subdivision_reject')}
        >
          {I18n.translate('fractions.subdivision.rejected')}
        </button>
      </Buttons>

      {detail && <Detail title={detail}>{detail}</Detail>}

      {errors.filter(Boolean).map((error) => (
        <Detail key={error} className='text-red'>
          {error}
        </Detail>
      ))}
    </Wrapper>
  );
};

export default observer(SubdivisionEvaluation);
